/**
 * Where Chat should land.
 *
 * Pressing Chat used to go to the bare #/chat route, which draws the empty
 * state, and only then did the view work out which conversation you were last
 * in and replace the address. That round trip is visible: an empty panel for a
 * frame, then the conversation - and pressing Chat twice in a row flickered it
 * twice.
 *
 * So the last conversation is remembered here and the nav goes straight to it.
 * Per-device and deliberately outside the account: which chat you had open on
 * this machine is not a setting worth syncing.
 */

const KEY = 'pogu.lastChat';

/** Held in memory too, so the nav does not touch storage on every render. */
let current: string | null = (() => {
  try { return localStorage.getItem(KEY); }
  catch { return null; }
})();

/**
 * Records the conversation you are in.
 *
 * Takes a full route path - "chat/ada" or "chat/g/g_xyz" - because that is
 * what the nav needs to hand to go(), and it keeps groups and people from
 * needing two different memories.
 */
export function rememberChat(path: string | null): void {
  if (path === current) return;
  current = path;
  try {
    if (path) localStorage.setItem(KEY, path);
    else localStorage.removeItem(KEY);
  } catch { /* storage off */ }
}

/**
 * Where the Chat button goes.
 *
 * Falls back to the plain route when there is nothing remembered - a first
 * run, or a conversation that was closed.
 */
export function chatRoute(): string {
  return current ?? 'chat';
}
